import { t } from '@/i18n/i18n'
import { AnalyticsService } from '@/pages/analytics/analyticsService'
import {
  type ApplicationConfig,
  type ApplicationConfigFontKeys,
  ApplicationConfigService,
  type TwilioConfig,
} from '@/shared/service/applicationConfig.service'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { getErrorMessage } from '@/shared/types/http.type'
import { create } from '@/shared/utils/zustandVue'
import { keys } from '@/utils'

import { useAppConfigStore } from '../../shared/store/app-config.store'

class ApplicationCfgState {
  applicationCfg: ApplicationConfig | null = null
  loading = false
  showAdvancedColors = false

  availableFonts: string[] = []
  fontChanges: { [k in ApplicationConfigFontKeys]?: File } = {}
}

type ApplicationCfgStore = ApplicationCfgState & {
  setApplicationCfg: (applicationCfg: ApplicationConfig) => void
  setShowAdvancedColors: (showAdvancedColors: boolean) => void
  load: () => void
  unload: () => void
  setFontChange: (payload: {
    file: File
    key: ApplicationConfigFontKeys
  }) => void
  loadApplicationCfg: () => Promise<void>
  updateApplicationCfg: (applicationCfg: ApplicationConfig) => Promise<void>
  updateApplicationCfgFeatures: (
    applicationCfg: ApplicationConfig,
  ) => Promise<void>
  updateApplicationCfgTwilio: (twilioConfig: TwilioConfig) => Promise<void>
  shiftDates: () => Promise<void>
  forceMatomoInit: () => Promise<void>
}

export const useApplicationCfgStore = create<ApplicationCfgStore>(
  (set, get) => ({
    ...new ApplicationCfgState(),
    setApplicationCfg: (applicationCfg) => {
      set({ applicationCfg })
    },
    setShowAdvancedColors: (showAdvancedColors) => {
      set({ showAdvancedColors })
    },
    load: () => {
      set({ loading: true })
    },
    unload: () => {
      set({ loading: false })
    },
    setFontChange: ({ file, key }) => {
      set((state) => ({
        fontChanges: { ...state.fontChanges, [key]: file },
      }))
    },
    async loadApplicationCfg() {
      try {
        get().load()
        const applicationCfg = await ApplicationConfigService.get()
        get().setApplicationCfg(applicationCfg)
        useAppConfigStore.getState().setAppConfig(applicationCfg)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        get().unload()
      }
    },
    async updateApplicationCfg(applicationCfg: ApplicationConfig) {
      try {
        get().load()
        await Promise.all(
          keys(get().fontChanges).map(async (key) => {
            const font = applicationCfg[key]
            const file = get().fontChanges[key]
            if (font != null && file != null) {
              const newFontId = await FilesService.uploadFont(file)
              font.id = newFontId.filename
              font.postScriptName = newFontId.postScriptName
            }
          }),
        )
        await ApplicationConfigService.updateApplicationCfg(applicationCfg)
        SnackbarService.info(t('APPLICATION_CFG.SAVE'))
        await get().loadApplicationCfg()
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        get().unload()
      }
    },
    async updateApplicationCfgFeatures(applicationCfg: ApplicationConfig) {
      try {
        get().load()
        await ApplicationConfigService.updateApplicationCfgFeatures(
          applicationCfg,
        )
        SnackbarService.info(t('APPLICATION_CFG.SAVE'))
        await get().loadApplicationCfg()
      } catch (e) {
        SnackbarService.error(getErrorMessage(e, t('GENERAL.SNACKBAR_ERROR')))
      } finally {
        get().unload()
      }
    },
    async updateApplicationCfgTwilio(twilioConfig: TwilioConfig) {
      try {
        get().load()
        await ApplicationConfigService.updateApplicationCfgTwilio(twilioConfig)
        SnackbarService.info(t('APPLICATION_CFG.SAVE'))
        await get().loadApplicationCfg()
      } catch (e) {
        SnackbarService.error(getErrorMessage(e, t('GENERAL.SNACKBAR_ERROR')))
      } finally {
        get().unload()
      }
    },
    async shiftDates() {
      try {
        get().load()
        await ApplicationConfigService.shiftDates()
        SnackbarService.info(t('APPLICATION_CFG.SAVE'))
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        get().unload()
      }
    },
    async forceMatomoInit() {
      await AnalyticsService.init()
      await get().loadApplicationCfg()
    },
  }),
)
